// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { AvailableDevices, MediaDeviceInfoType, SelectedDevices } from '../types/Video';
import { createVideoRoomToken } from './createVideoRoomToken';
import { getDefaultDevice } from './getDefaultDevice';
import { createVideoRoom } from './createVideoRoom';
import { getMediaDevices } from './getMediaDevices';
import { useEffect, useRef, useState } from 'react';
import { initialize, Room } from '@telnyx/video';
import { isSafari } from 'utils/isSafari';
import { v4 as uuid4 } from 'uuid';
import { BrowserAgent } from '@newrelic/browser-agent';
import { logger } from 'utils/logger';

const useVideoRoom = (isUpgrade = false) => {
  const [roomId, setRoomId] = useState(null);
  const [isRoomInitialized, setIsRoomInitialized] = useState(false);
  const [localVideoTrack, setLocalVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [localBlurredVideoTrack, setLocalBlurredVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [microphoneMuted, setMicrophoneMuted] = useState<boolean>(false);
  const [cameraMuted, setCameraMuted] = useState<boolean>(false);
  const [isVideoBlurred, setIsVideoBlurred] = useState(false);
  const [preCallBlurInitialized, setPreCallBlurInitialized] = useState(false);
  const [inCallBlurInitialized, setInCallBlurInitialized] = useState(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false);

  const [availableDevices, setAvailableDevices] = useState<AvailableDevices>({
    audioInputs: [],
    audioOutputs: [],
    videoInputs: [],
  });
  const [selectedDevices, setSelectedDevices] = useState<SelectedDevices>({
    audioInput: null,
    audioOutput: null,
    videoInput: null,
  });
  const roomRef = useRef<Room>(null);
  const videoStatsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const finalStatsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const callStatsRef = useRef({
    audio_packets_lost: 0,
    audio_packets_received: 0,
    audio_jitterSum: 0,
    audio_jitterCount: 0,
    audio_rttSum: 0,
    audio_rttCount: 0,
    video_packets_lost: 0,
    video_packets_received: 0,
    video_jitterSum: 0,
    video_jitterCount: 0,
    video_rttSum: 0,
    video_rttCount: 0,
  });

  const processVideoStat = (stats: any) => {
    const videoStats = stats.receivers?.video;
    if (videoStats) {
      for (let statId in videoStats) {
        processStat(videoStats[statId], true);
      }
    }
  };

  const processStat = (stat: any, video = false) => {
    switch (stat.type) {
      case 'inbound-rtp':
        if (stat.kind === 'audio') {
          callStatsRef.current.audio_packets_lost += stat.packetsLost || 0;
          callStatsRef.current.audio_packets_received += stat.packetsReceived || 0;
          if (stat.jitter !== undefined) {
            callStatsRef.current.audio_jitterSum += stat.jitter * 1000;
            callStatsRef.current.audio_jitterCount++;
          }
        } else if (stat.kind === 'video') {
          callStatsRef.current.video_packets_lost += stat.packetsLost || 0;
          callStatsRef.current.video_packets_received += stat.packetsReceived || 0;
          if (stat.jitter !== undefined) {
            callStatsRef.current.video_jitterSum += stat.jitter * 1000;
            callStatsRef.current.video_jitterCount++;
          }
        }
        break;
      case 'candidate-pair':
        if (stat.nominated && stat.state === 'succeeded') {
          if (!video) {
            if (stat.currentRoundTripTime !== undefined) {
              const rttMs = stat.currentRoundTripTime * 1000;
              callStatsRef.current.audio_rttSum += rttMs;
              callStatsRef.current.audio_rttCount++;
            }
          } else if (video) {
            if (stat.currentRoundTripTime !== undefined) {
              const rttMs = stat.currentRoundTripTime * 1000;
              callStatsRef.current.video_rttSum += rttMs;
              callStatsRef.current.video_rttCount++;
            }
          }
        }
        break;
      default:
        break;
    }
  };

  const calculateFinalStats = () => {
    const audio_jitter = callStatsRef.current.audio_jitterCount > 0
      ? callStatsRef.current.audio_jitterSum / callStatsRef.current.audio_jitterCount
      : 0;
    const audio_round_trip_time = callStatsRef.current.audio_rttCount > 0
      ? callStatsRef.current.audio_rttSum / callStatsRef.current.audio_rttCount
      : 0;

    const video_jitter = callStatsRef.current.video_jitterCount > 0
      ? callStatsRef.current.video_jitterSum / callStatsRef.current.video_jitterCount
      : 0;
    const video_round_trip_time = callStatsRef.current.video_rttCount > 0
      ? callStatsRef.current.video_rttSum / callStatsRef.current.video_rttCount
      : 0;

    return {
      session_id: roomId,
      visitor_id: 'VideoApp',

      audio_packets_lost: callStatsRef.current.audio_packets_lost,
      audio_packets_received: callStatsRef.current.audio_packets_received,
      audio_jitter: audio_jitter,
      audio_round_trip_time: audio_round_trip_time,

      video_packets_lost: callStatsRef.current.video_packets_lost,
      video_packets_received: callStatsRef.current.video_packets_received,
      video_jitter: video_jitter,
      video_round_trip_time: video_round_trip_time,
    };
  };

  const sendFinalStats = async () => {
    const webrtcStats = calculateFinalStats();
    console.log('WebRTC Final Stats:', webrtcStats);
    await sendWebRTCStats(webrtcStats);
  };

  const sendWebRTCStats = async (stats: any) => {
    const apiUrl = process.env.REACT_APP_WEBRTC_STATS_API_URL;

    if (!apiUrl) {
      console.warn('REACT_APP_WEBRTC_STATS_API_URL not configured, skipping stats send');
      return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stats),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      if (responseText.trim()) {
        try {
          const result = JSON.parse(responseText);
          console.log('WebRTC stats sent successfully:', result);
        } catch (parseError) {
          console.log('WebRTC stats sent successfully (non-JSON response):', responseText);
        }
      } else {
        console.log('WebRTC stats sent successfully (empty response)');
      }
    } catch (error) {
      console.error('Error sending WebRTC stats:', error);
    }
  };

  const cleanupStats = () => {
    if (videoStatsIntervalRef.current) {
      clearInterval(videoStatsIntervalRef.current);
      videoStatsIntervalRef.current = null;
    }
    if (finalStatsIntervalRef.current) {
      clearInterval(finalStatsIntervalRef.current);
      finalStatsIntervalRef.current = null;
    }
  };

  const resetStats = () => {
    callStatsRef.current = {
      audio_packets_lost: 0,
      audio_packets_received: 0,
      audio_jitterSum: 0,
      audio_jitterCount: 0,
      audio_rttSum: 0,
      audio_rttCount: 0,
      video_packets_lost: 0,
      video_packets_received: 0,
      video_jitterSum: 0,
      video_jitterCount: 0,
      video_rttSum: 0,
      video_rttCount: 0,
    };
  };

  const canUpdateStream = () => {
    if (!roomRef.current || !isRoomInitialized) {
      console.log('Room not initialized yet, cannot update stream');
      return false;
    }

    const roomState = roomRef.current.getState();
    const visitorPublisher = roomState.publishers.get('visitor');

    if (!visitorPublisher) {
      console.log('Visitor publisher does not exist yet, cannot update stream');
      return false;
    }

    return true;
  };

  const initUpgradeRoom = async (accountId: number, roomId: string | null) => {
    try {
      logger.setAccountId(accountId.toString());
      logger.info('Starting initUpgradeRoom', 'initUpgradeRoom', {
        accountId,
        roomId,
        isUpgrade: true
      });

      //create room
      const currentRoomId = roomId;
      setRoomId(currentRoomId);

      logger.info('Creating video room token', 'initUpgradeRoom', {
        roomId: currentRoomId,
        accountId
      });

      let roomTokenResponse = await createVideoRoomToken(currentRoomId, accountId);
      const clientToken = roomTokenResponse.token;
      const context = 'Visitor';

      logger.info('Video room token created successfully', 'initUpgradeRoom', {
        hasToken: !!clientToken,
        context
      });

      //initialize room
      logger.info('Initializing video room', 'initUpgradeRoom', {
        roomId: currentRoomId,
        context
      });

      const room = await initialize({
        roomId: currentRoomId,
        clientToken,
        context,
      });

      logger.info('Video room initialized successfully', 'initUpgradeRoom', {
        roomId: currentRoomId
      });

      //on connected
      room.on('connected', async () => {
        logger.info('Room connected successfully', 'initUpgradeRoom', {
          roomId: currentRoomId
        });
        roomRef.current = room;
        setIsRoomInitialized(true);
        try {
          let localVideoTrack: MediaStreamTrack | null = null;

          logger.info('Requesting user media for upgrade room', 'initUpgradeRoom', {
            video: true,
            audio: false
          });

          try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false,
            });
            localVideoTrack = mediaStream.getVideoTracks()[0] || null;
            setCameraPermissionDenied(false);
            logger.info('User media obtained successfully', 'initUpgradeRoom', {
              hasVideoTrack: !!localVideoTrack,
              trackId: currentRoomId
            });
          } catch (error) {
            console.error('Error accessing video device:', error);
            logger.error('Error accessing video device', 'initUpgradeRoom', {
              error: error.message,
              stackTrace: error.stack
            });
            JSErrors.noticeError(error);
            try {
              const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
              localVideoTrack = videoStream.getVideoTracks()[0] || null;
              setCameraPermissionDenied(false);
              logger.info('Retry video access successful', 'initUpgradeRoom', {
                hasVideoTrack: !!localVideoTrack,
                trackId: currentRoomId
              });
            } catch (videoError) {
              if (videoError?.name === 'NotAllowedError') {
                setCameraPermissionDenied(true);
              }
              console.error('Video device not found or not accessible:', videoError);
              logger.error('Video device not accessible after retry', 'initUpgradeRoom', {
                error: videoError.message,
                stackTrace: videoError.stack
              });
              JSErrors.noticeError(videoError);
            }
          }

          if (localVideoTrack) {
            setLocalVideoTrack(localVideoTrack);
            logger.info('Local video track set in state', 'initUpgradeRoom', {
              trackId: currentRoomId
            });
          }

          if (localVideoTrack) {
            logger.info('Adding video stream to room', 'initUpgradeRoom', {
              streamKey: 'visitor',
              trackId: currentRoomId
            });
            await room.addStream('visitor', {
              video: localVideoTrack,
            });
            logger.info('Video stream added to room successfully', 'initUpgradeRoom');
          } else {
            console.error('No video tracks available to add to the room.');
            logger.error('No video tracks available to add to room', 'initUpgradeRoom');
          }

          const state = room.getState();
          const agentParticipant = Array.from(state.participants.values()).find(
            (obj) => obj.origin != 'local',
          );

          logger.info('Checking for agent participant', 'initUpgradeRoom', {
            hasAgentParticipant: !!agentParticipant,
            participantCount: state.participants.size
          });

          if (agentParticipant) {
            try {
              logger.info('Subscribing to agent stream', 'initUpgradeRoom', {
                agentId: agentParticipant.id,
                streamKey: 'agent'
              });
              await room.addSubscription(agentParticipant.id, 'agent', {
                video: true,
              });
              console.log('Successfully subscribed to agent stream');
              logger.info('Successfully subscribed to agent stream', 'initUpgradeRoom', {
                agentId: agentParticipant.id
              });
            } catch (error) {
              console.error('Failed to subscribe to agent stream, will retry when stream is published:', error);
              logger.warn('Failed to subscribe to agent stream, will retry', 'initUpgradeRoom', {
                agentId: agentParticipant.id,
                error: error.message
              });
            }
          }
        } catch (error) {
          console.error('Error accessing user media:', error);
          logger.error('Error accessing user media', 'initUpgradeRoom', {
            error: error.message,
            stackTrace: error.stack
          });
        }
      });

      //when agent joined
      room.on('participant_joined', async (participantId, state) => {
        const remoteParticipant = state.participants.get(participantId);
        console.log(`participant ${remoteParticipant.context} joined...`, state);
        
        logger.info('Participant joined', 'initUpgradeRoom', {
          participantId,
          context: remoteParticipant.context,
          origin: remoteParticipant.origin
        });

        const audioElement = document.querySelector('audio');
        if (audioElement) {
          audioElement.muted = true;
        }

        if (remoteParticipant && remoteParticipant.origin !== 'local') {
          try {
            logger.info('Subscribing to agent stream for new participant', 'initUpgradeRoom', {
              participantId,
              streamKey: 'agent'
            });
            await room.addSubscription(participantId, 'agent', {
              video: true,
            });
            console.log(`Subscribed to agent stream for participant ${participantId}`);
            logger.info('Successfully subscribed to agent stream for new participant', 'initUpgradeRoom', {
              participantId
            });
          } catch (error) {
            console.warn(`Failed to subscribe to agent stream for participant ${participantId}, will retry when stream is published:`, error);
            logger.warn('Failed to subscribe to agent stream for new participant', 'initUpgradeRoom', {
              participantId,
              error: error.message
            });
          }
        }
      });

      //when agent stream is published
      room.on('stream_published', async (participantId, streamKey, state) => {
        console.log(`stream published for participant ${participantId} with key ${streamKey}`, state);
        const remoteParticipant = state.participants.get(participantId);
        
        logger.info('Stream published', 'initUpgradeRoom', {
          participantId,
          streamKey,
          origin: remoteParticipant?.origin
        });
        
        if (remoteParticipant && remoteParticipant.origin !== 'local') {
          try {
            logger.info('Subscribing to published stream', 'initUpgradeRoom', {
              participantId,
              streamKey
            });
            await room.addSubscription(participantId, streamKey, {
              video: true,
            });
            console.log(`Successfully subscribed to ${streamKey} stream for participant ${participantId}`);
            logger.info('Successfully subscribed to published stream', 'initUpgradeRoom', {
              participantId,
              streamKey
            });
          } catch (error) {
            console.error(`Failed to subscribe to ${streamKey} stream for participant ${participantId}:`, error);
            logger.error('Failed to subscribe to published stream', 'initUpgradeRoom', {
              participantId,
              streamKey,
              error: error.message
            });
          }
        }
      });

      //when subscription is started
      room.on('subscription_started', (participantId, streamKey, state) => {
        console.log(`subscription to the: ${participantId} ${streamKey} stream started...`, state);

        logger.info('Subscription started', 'initUpgradeRoom', {
          participantId,
          streamKey
        });

        try {
          const remoteStream = room.getParticipantStream(participantId, streamKey);

          if (remoteStream) {
            if (remoteStream.videoTrack) {
              setRemoteVideoTrack(remoteStream.videoTrack);
              logger.info('Remote video track set', 'initUpgradeRoom', {
                participantId,
                streamKey,
                trackId: currentRoomId
              });
            }
            console.log('Successfully subscribed to agent stream');
            logger.info('Successfully subscribed to agent stream', 'initUpgradeRoom', {
              participantId,
              streamKey,
              hasVideoTrack: !!remoteStream.videoTrack
            });
          } else {
            console.warn(`No stream found for participant ${participantId} with key ${streamKey}`);
            logger.warn('No stream found for participant', 'initUpgradeRoom', {
              participantId,
              streamKey
            });
          }
        } catch (error) {
          console.error(`Error getting participant stream for ${participantId} with key ${streamKey}:`, error);
          logger.error('Error getting participant stream', 'initUpgradeRoom', {
            participantId,
            streamKey,
            error: error.message
          });
        }

        // Start collecting WebRTC statistics
        videoStatsIntervalRef.current = setInterval(() => {
          if (roomRef.current) {
            roomRef.current
              .getWebRTCStatsForStream(participantId, streamKey)
              .then((stats) => {
                console.log('WebRTC Video stats:', stats);
                processVideoStat(stats);
              })
              .catch((error) => {
                if (error.message && error.message.includes('No stream exists')) {
                  console.warn('WebRTC stats not available - stream may not be ready yet:', error.message);
                } else {
                  console.error('Error fetching WebRTC stats:', error);
                }
              });
          }
        }, 2000);

        finalStatsIntervalRef.current = setInterval(() => {
          sendFinalStats().catch((error) => {
            console.error('Error sending final stats:', error);
          });
        }, 10000);

        window.addEventListener("beforeunload", (event) => {
          sendFinalStats().catch((error) => {
            console.error('Error sending final stats:', error);
          });
        })

      });

      room.on('notification', (notification) => {
        if (notification.call?.state === 'hangup') {
          sendFinalStats().catch((error) => {
            console.error('Error sending final stats:', error);
          });
        }
      });

      room.on('disconnected', () => {
        sendFinalStats().catch((error) => {
          console.error('Error sending final stats:', error);
        });
        console.log('Room disconnected');
        cleanupStats();
      });


      logger.info('Connecting to room', 'initUpgradeRoom', {
        roomId: currentRoomId
      });

      await room.connect();
      console.log('Room connected successfully!');
      
      logger.info('Room connection completed successfully', 'initUpgradeRoom', {
        roomId: currentRoomId
      });

    } catch (error) {
      console.error('Error creating room and initializing:', error);
      logger.error('Error creating room and initializing', 'initUpgradeRoom', {
        error: error.message,
        stackTrace: error.stack,
        roomId: currentRoomId
      });
    }
  };

  const initRoom = async (meetId: string, accountId: number, roomId: string | null) => {
    try {
      resetStats();
      let currentRoomId = roomId;
      if (currentRoomId) {
        setRoomId(currentRoomId);
      } else {
        // Create room name
        const uuid = uuid4();
        const roomName = `${meetId}_${uuid}`;

        // Create room
        let roomResponse = await createVideoRoom(roomName, accountId);
        currentRoomId = roomResponse.room_id;
        setRoomId(currentRoomId);
      }

      // Get token
      let roomResponse = await createVideoRoomToken(currentRoomId, accountId);
      const clientToken = roomResponse.token;
      const context = 'Visitor';

      // Initialize room
      const room = await initialize({
        roomId: currentRoomId,
        clientToken,
        context,
      });

      room.on('connected', async () => {
        roomRef.current = room;
        setIsRoomInitialized(true);
        try {
          let localAudioTrack: MediaStreamTrack | null = null;
          let localVideoTrack: MediaStreamTrack | null = null;

          try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
              audio: !isUpgrade,
              video: true,
            });

            if (!isUpgrade) {
              localAudioTrack = mediaStream.getAudioTracks()[0] || null;
            }
            localVideoTrack = mediaStream.getVideoTracks()[0] || null;
          } catch (combinedError) {
            console.warn('Unable to access both audio and video devices together. Trying separately...', combinedError);

            if (!isUpgrade) {
              try {
                const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                localAudioTrack = audioStream.getAudioTracks()[0] || null;
              } catch (audioError) {
                console.warn('Audio device not found or not accessible:', audioError);
              }
            }
            try {
              const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
              localVideoTrack = videoStream.getVideoTracks()[0] || null;
            } catch (videoError) {
              console.warn('Video device not found or not accessible:', videoError);
            }
          }

          if (localAudioTrack) {
            setLocalAudioTrack(localAudioTrack);
          }

          if (localVideoTrack) {
            // Sprawdź czy obraz z kamery nie jest czarny
            const isBlackVideo = async (track) => {
              return new Promise((resolve) => {
                const video = document.createElement('video');
                video.srcObject = new MediaStream([track]);
                video.muted = true;
                video.play();
                let tries = 0;
                const check = () => {
                  tries++;
                  if ((video.videoWidth > 10 && video.videoHeight > 10) || tries > 10) {
                    resolve(!(video.videoWidth > 10 && video.videoHeight > 10));
                  } else {
                    setTimeout(check, 100);
                  }
                };
                check();
              });
            };

            let retryCount = 0;
            let maxRetries = 2;
            let black = await isBlackVideo(localVideoTrack);
            while (black && retryCount < maxRetries) {
              // Zatrzymaj poprzedni track
              localVideoTrack.stop();
              // Spróbuj ponownie pobrać stream
              const retryStream = await navigator.mediaDevices.getUserMedia({ video: true });
              const retryTrack = retryStream.getVideoTracks()[0] || null;
              if (retryTrack) {
                black = await isBlackVideo(retryTrack);
                if (!black) {
                  localVideoTrack = retryTrack;
                  setLocalVideoTrack(localVideoTrack);
                  break;
                } else {
                  retryTrack.stop();
                }
              }
              retryCount++;
            }
            setLocalVideoTrack(localVideoTrack);
          }

          if (localAudioTrack || localVideoTrack) {
            await room.addStream('visitor', {
              audio: localAudioTrack || undefined,
              video: localVideoTrack || undefined,
            });
          } else {
            console.error('No audio or video tracks available to add to the room.');
          }

          const state = room.getState();
          const agentParticipant = Array.from(state.participants.values()).find(
            (obj) => obj.origin != 'local',
          );

          if (agentParticipant) {
            try {
              await room.addSubscription(agentParticipant.id, 'agent', {
                audio: true,
                video: true,
              });
            } catch (error) {
              console.warn('Failed to subscribe to agent stream, will retry when stream is published:', error);
            }
          }
        } catch (error) {
          console.error('Error accessing user media:', error);
        }
      });

      room.on('participant_joined', async (participantId, state) => {
        const remoteParticipant = state.participants.get(participantId);
        console.log(`participant ${remoteParticipant.context} joined...`, state);
        const audioElement = document.querySelector('audio');
        if (audioElement) {
          audioElement.muted = true;
        }

        if (remoteParticipant && remoteParticipant.origin !== 'local') {
          try {
            await room.addSubscription(participantId, 'agent', {
              audio: true,
              video: true,
            });
            console.log(`Subscribed to agent stream for participant ${participantId}`);
          } catch (error) {
            console.warn(`Failed to subscribe to agent stream for participant ${participantId}, will retry when stream is published:`, error);
          }
        }
      });

      room.on('stream_published', async (participantId, streamKey, state) => {
        const participant = state.participants.get(participantId);
        if (participant.origin === 'local') return;

        try {
          await room.addSubscription(participantId, streamKey, {
            audio: true,
            video: true,
          });
          console.log(`Successfully subscribed to ${streamKey} stream for participant ${participantId}`);
        } catch (error) {
          console.error(`Failed to subscribe to ${streamKey} stream for participant ${participantId}:`, error);
        }
      });

      room.on('subscription_started', (participantId, streamKey, state) => {
        console.log(`subscription to the: ${participantId} ${streamKey} stream started...`, state);

        try {
          const remoteStream = room.getParticipantStream(participantId, streamKey);

          if (remoteStream) {
            if (remoteStream.audioTrack) setRemoteAudioTrack(remoteStream.audioTrack);
            if (remoteStream.videoTrack) setRemoteVideoTrack(remoteStream.videoTrack);
          } else {
            console.warn(`No stream found for participant ${participantId} with key ${streamKey}`);
          }
        } catch (error) {
          console.error(`Error getting participant stream for ${participantId} with key ${streamKey}:`, error);
        }

        // Start collecting WebRTC statistics
        videoStatsIntervalRef.current = setInterval(() => {
          if (roomRef.current) {
            roomRef.current
              .getWebRTCStatsForStream(participantId, streamKey)
              .then((stats) => {
                console.log('WebRTC Video stats:', stats);
                processVideoStat(stats);
              })
              .catch((error) => {
                if (error.message && error.message.includes('No stream exists')) {
                  console.warn('WebRTC stats not available - stream may not be ready yet:', error.message);
                } else {
                  console.error('Error fetching WebRTC stats:', error);
                }
              });
          }
        }, 2000);

        finalStatsIntervalRef.current = setInterval(() => {
          sendFinalStats().catch((error) => {
            console.error('Error sending final stats:', error);
          });
        }, 10000);

        window.addEventListener("beforeunload", (event) => {
          sendFinalStats().catch((error) => {
            console.error('Error sending final stats:', error);
          });
        })

      });

      room.on('notification', (notification) => {
        if (notification.call?.state === 'hangup') {
          sendFinalStats().catch((error) => {
            console.error('Error sending final stats:', error);
          });
        }
      });

      room.on('disconnected', () => {
        sendFinalStats().catch((error) => {
          console.error('Error sending final stats:', error);
        });
        console.log('Room disconnected');
        cleanupStats();
      });


      await room.connect();
    } catch (error) {
      console.error('Failed to initialize the room:', error);
    }
  };

  const applyBlurOnPreCallVideo = async () => {
    if (!preCallBlurInitialized) {
      const blurredVideo = await blurVideo('preCall');
      await roomRef.current?.updateStream('visitor', {
        video: blurredVideo,
        audio: localAudioTrack ? localAudioTrack : undefined,
      });
      setLocalBlurredVideoTrack(blurredVideo);
      setIsVideoBlurred(true);
      setPreCallBlurInitialized(true);
      return;
    }

    if (!isVideoBlurred) {
      await roomRef.current?.updateStream('visitor', {
        video: localBlurredVideoTrack ? localBlurredVideoTrack : undefined,
        audio: localAudioTrack ? localAudioTrack : undefined,
      });
      setIsVideoBlurred(true);
    }

    if (isVideoBlurred) {
      await roomRef.current?.updateStream('visitor', {
        video: localVideoTrack ? localVideoTrack : undefined,
        audio: localAudioTrack ? localAudioTrack : undefined,
      });
      setIsVideoBlurred(false);
    }
  };

  const applyBlurOnInCallVideo = async () => {
    if (!inCallBlurInitialized) {
      const blurredVideo = await blurVideo('inCall');

      await roomRef.current?.updateStream('visitor', {
        video: blurredVideo,
        audio: localAudioTrack ? localAudioTrack : undefined,
      });

      setLocalBlurredVideoTrack(blurredVideo);
      setIsVideoBlurred(true);
      setInCallBlurInitialized(true);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 300));

    if (!isVideoBlurred) {
      await roomRef.current?.updateStream('visitor', {
        video: localBlurredVideoTrack || undefined,
        audio: localAudioTrack || undefined,
      });
      setIsVideoBlurred(true);
    } else {
      await roomRef.current?.updateStream('visitor', {
        video: localVideoTrack || undefined,
        audio: localAudioTrack || undefined,
      });
      setIsVideoBlurred(false);
    }
  };

  const blurVideo = async (state: 'preCall' | 'inCall') => {
    const video = document.getElementById(state === 'preCall' ? 'localVideoPreCall' : 'localVideoInCall');
    const canvas = document.getElementById(state === 'preCall' ? 'localVideoPreCallCanvas' : 'localVideoInCallCanvas');

    const canvasCtx = canvas.getContext('2d')

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const selfieSegmentation = new SelfieSegmentation({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
    })

    selfieSegmentation.setOptions({
      modelSelection: 1,
    })

    const onResults = (results) => {
      if (isSafari()) {
        const backgroundCanvas = document.createElement('canvas')
        const backgroundCtx = backgroundCanvas.getContext('2d')
        backgroundCanvas.width = canvas.width
        backgroundCanvas.height = canvas.height

        const foregroundCanvas = document.createElement('canvas')
        const foregroundCtx = foregroundCanvas.getContext('2d')
        foregroundCanvas.width = canvas.width
        foregroundCanvas.height = canvas.height

        const offsets = [
          { x: -10, y: -10 },
          { x: 10, y: 10 },
          { x: -5, y: 5 },
          { x: 5, y: -5 },
          { x: 0, y: 0 },
        ]

        offsets.forEach((offset) => {
          backgroundCtx.globalAlpha = 0.15
          backgroundCtx.drawImage(results.image, offset.x, offset.y, canvas.width, canvas.height)
        })
        backgroundCtx.globalAlpha = 1.0

        const imageData = backgroundCtx.getImageData(0, 0, backgroundCanvas.width, backgroundCanvas.height)
        const data = imageData.data

        for (let i = 0; i < data.length; i += 4) {
          const noise = (Math.random() - 0.5) * 30
          data[i] = Math.min(Math.max(data[i] + noise, 0), 255)
          data[i + 1] = Math.min(Math.max(data[i + 1] + noise, 0), 255)
          data[i + 2] = Math.min(Math.max(data[i + 2] + noise, 0), 255)
        }

        backgroundCtx.putImageData(imageData, 0, 0)
        backgroundCtx.fillStyle = 'rgba(0, 0, 0, 0.3)'
        backgroundCtx.fillRect(0, 0, backgroundCanvas.width, backgroundCanvas.height)

        foregroundCtx.drawImage(results.segmentationMask, 0, 0, canvas.width, canvas.height)
        foregroundCtx.globalCompositeOperation = 'source-in'
        foregroundCtx.drawImage(results.image, 0, 0, canvas.width, canvas.height)

        canvasCtx.clearRect(0, 0, canvas.width, canvas.height)
        canvasCtx.drawImage(backgroundCanvas, 0, 0)
        canvasCtx.drawImage(foregroundCanvas, 0, 0)

        requestAnimationFrame(() => selfieSegmentation.send({ image: video }))
      } else {
        canvasCtx.save()
        canvasCtx.clearRect(0, 0, canvas.width, canvas.height)

        canvasCtx.drawImage(results.segmentationMask, 0, 0, canvas.width, canvas.height)
        canvasCtx.globalCompositeOperation = 'source-in'
        canvasCtx.drawImage(results.image, 0, 0, canvas.width, canvas.height)
        canvasCtx.globalCompositeOperation = 'destination-atop'

        canvasCtx.filter = 'blur(20px)'
        canvasCtx.drawImage(results.image, 0, 0, canvas.width, canvas.height)
        canvasCtx.restore()

        requestAnimationFrame(() => selfieSegmentation.send({ image: video }))
      }
    }

    const processVideoFrames = () => {
      selfieSegmentation.onResults(onResults)
      selfieSegmentation.send({ image: video })
    }

    processVideoFrames()


    const canvasStream = canvas.captureStream(30);
    return canvasStream.getVideoTracks()[0];
  };

  const TurnCameraOnInCallVideo = async () => {
    if (!localVideoTrack) return;
    const currentFacingMode = localVideoTrack.getSettings().facingMode;
    const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';

    if (localVideoTrack) {
      localVideoTrack.stop();
    }

    if (localBlurredVideoTrack) {
      localBlurredVideoTrack.stop();
    }

    try {
      const constraints = {
        audio: false,
        video: {
          facingMode: {
            exact: newFacingMode,
          },
        },
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      const newVideoTrack = newStream.getVideoTracks()[0];

      await roomRef.current?.updateStream('visitor', {
        video: newVideoTrack,
        audio: localAudioTrack || undefined,
      });

      if (localBlurredVideoTrack) {
        localBlurredVideoTrack.stop();
      }

      setLocalVideoTrack(newVideoTrack);
      setLocalBlurredVideoTrack(null);
      setIsVideoBlurred(false);
      setInCallBlurInitialized(false);
    } catch (error) {
      console.error('Error switching camera:', error);
      alert('Could not switch the camera. Please try again.');
    }
  };

  const toggleMicrophone = async () => {
    if (!localAudioTrack && isUpgrade) {
      // Pobierz stream audio i video po kliknięciu, jeśli to jest upgrade
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        const newAudioTrack = mediaStream.getAudioTracks()[0];
        let newVideoTrack = mediaStream.getVideoTracks()[0];
        // Sprawdź czy obraz z kamery nie jest czarny (jak w on connected)
        const isBlackVideo = async (track) => {
          return new Promise((resolve) => {
            const video = document.createElement('video');
            video.srcObject = new MediaStream([track]);
            video.muted = true;
            video.play();
            let tries = 0;
            const check = () => {
              tries++;
              if ((video.videoWidth > 10 && video.videoHeight > 10) || tries > 10) {
                resolve(!(video.videoWidth > 10 && video.videoHeight > 10));
              } else {
                setTimeout(check, 100);
              }
            };
            check();
          });
        };
        let retryCount = 0;
        let maxRetries = 2;
        let black = newVideoTrack ? await isBlackVideo(newVideoTrack) : false;
        while (black && retryCount < maxRetries) {
          newVideoTrack && newVideoTrack.stop();
          const retryStream = await navigator.mediaDevices.getUserMedia({ video: true });
          const retryTrack = retryStream.getVideoTracks()[0] || null;
          if (retryTrack) {
            black = await isBlackVideo(retryTrack);
            if (!black) {
              newVideoTrack = retryTrack;
              break;
            } else {
              retryTrack.stop();
            }
          }
          retryCount++;
        }
        if (newAudioTrack && newVideoTrack) {
          setLocalAudioTrack(newAudioTrack);
          setLocalVideoTrack(newVideoTrack);
          await roomRef.current?.updateStream('visitor', {
            audio: newAudioTrack,
            video: newVideoTrack,
          });
          setMicrophoneMuted(false);
        } else if (newAudioTrack) {
          setLocalAudioTrack(newAudioTrack);
          await roomRef.current?.updateStream('visitor', {
            audio: newAudioTrack,
            video: localVideoTrack || undefined,
          });
          setMicrophoneMuted(false);
        }
      } catch (err) {
        alert('Nie udało się uzyskać dostępu do mikrofonu lub kamery. Upewnij się, że mikrofon i kamera nie są używane przez inną aplikację.');
      }
      return;
    }
    if (localAudioTrack) {
      const isMuted = !microphoneMuted;
      setMicrophoneMuted(isMuted);
      localAudioTrack.enabled = !isMuted;
      console.log('Microphone is now:', isMuted ? 'Muted' : 'Unmuted');
    } else {
      console.error('No local audio track available to toggle.');
    }
  };

  const toggleCamera = () => {
    if (localVideoTrack) {
      const isMuted = !cameraMuted;
      setCameraMuted(isMuted);
      localVideoTrack.enabled = !isMuted;
      console.log('Camera is now:', isMuted ? 'Muted' : 'Unmuted');
    } else {
      console.error('No local video track available to toggle.');
    }
  };

  const updateDevice = async (device: MediaDeviceInfoType) => {
    try {
      const kind =
        device.kind === 'audioinput'
          ? 'audioInput'
          : device.kind === 'audiooutput'
            ? 'audioOutput'
            : device.kind === 'videoinput'
              ? 'videoInput'
              : null;

      if (!kind) {
        throw new Error('Unsupported device kind');
      }

      const newSelectedDevices = { ...selectedDevices, [kind]: device };

      if (kind === 'audioOutput') {
        // Handle audio output change
        const audioElement = document.querySelector('#localVideoPreCall');
        if (audioElement && 'setSinkId' in audioElement) {
          // @ts-ignore: Ignore TypeScript error for setSinkId not being found
          await audioElement.setSinkId(device.deviceId);
          console.log('Audio output device switched to:', device.label);
        }
      } else {
        const constraints =
          kind === 'audioInput'
            ? { audio: { deviceId: { exact: device.deviceId } } }
            : { video: { deviceId: { exact: device.deviceId } } };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        const newTrack = kind === 'audioInput' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0];

        if (!newTrack) throw new Error(`No ${kind} track found.`);

        if (kind === 'audioInput' && localAudioTrack) {
          localAudioTrack.stop();
          setLocalAudioTrack(newTrack);
        } else if (kind === 'videoInput' && localVideoTrack) {
          localVideoTrack.stop();
          setLocalVideoTrack(newTrack);
        }

        // Check if room is initialized and publisher exists before updating
        if (!canUpdateStream()) {
          return;
        }

        await roomRef.current.updateStream('visitor', {
          audio: kind === 'audioInput' ? newTrack : localAudioTrack ? localAudioTrack : stream.getAudioTracks()[0],
          video: kind === 'videoInput' ? newTrack : localVideoTrack ? localVideoTrack : stream.getVideoTracks()[0],
        });

        console.log(`${kind} switched to device:`, device.label);
      }

      setSelectedDevices(newSelectedDevices);
    } catch (error) {
      console.error('Failed to update device:', error);
    }
  };

  const setMediaDevices = async (update: boolean) => {
    const devices = await getMediaDevices();
    if (devices) {
      const selectedAudioInput = getDefaultDevice(devices.audioInputs);
      const selectedAudioOutput = getDefaultDevice(devices.audioOutputs);
      const selectedVideoInput = getDefaultDevice(devices.videoInputs);

      if (update) {
        // Check if room is initialized and publisher exists before updating
        if (!canUpdateStream()) {
          setSelectedDevices({
            audioInput: selectedAudioInput,
            audioOutput: selectedAudioOutput,
            videoInput: selectedVideoInput,
          });
          setAvailableDevices(devices || availableDevices);
          return;
        }

        const constraints = {
          audio: selectedAudioInput ? { deviceId: { exact: selectedAudioInput.deviceId } } : false,
          video: selectedVideoInput ? { deviceId: { exact: selectedVideoInput.deviceId } } : false,
        };

        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);

          await roomRef.current.updateStream('visitor', {
            audio: stream.getAudioTracks()[0] || null,
            video: stream.getVideoTracks()[0] || null,
          });
          setLocalVideoTrack(stream.getVideoTracks()[0]);
          setLocalAudioTrack(stream.getAudioTracks()[0]);
        } catch (err) {
          console.error('Error updating media stream:', err);
        }
      }

      setSelectedDevices({
        audioInput: selectedAudioInput,
        audioOutput: selectedAudioOutput,
        videoInput: selectedVideoInput,
      });
    }

    setAvailableDevices(devices || availableDevices);
  };

  useEffect(() => {
    setMediaDevices(false);

    const handleDeviceChange = () => {
      console.log('Device change detected');
      setMediaDevices(true);
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  type ExtendedPermissionName = PermissionName | 'microphone' | 'camera';

  useEffect(() => {
    const monitorPermissions = async () => {
      try {
        const micPermission = await navigator.permissions.query({
          name: 'microphone' as ExtendedPermissionName,
        } as PermissionDescriptor);
        const camPermission = await navigator.permissions.query({
          name: 'camera' as ExtendedPermissionName,
        } as PermissionDescriptor);

        micPermission.onchange = () => {
          console.log('Microphone permission changed:', micPermission.state);
          if (micPermission.state === 'granted') {
            setMediaDevices(true);
          }
        };

        camPermission.onchange = () => {
          console.log('Camera permission changed:', camPermission.state);
          if (camPermission.state === 'granted') {
            setCameraPermissionDenied(false);
            setMediaDevices(true);
          } else if (camPermission.state === 'denied') {
            setCameraPermissionDenied(true);
          }
        };
      } catch (error) {
        console.error('Error monitoring permissions:', error);
      }
    };

    const handleDeviceChange = () => {
      console.log('Device change detected');
      setMediaDevices(true);
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    monitorPermissions();

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);

      navigator.permissions.query({ name: 'microphone' } as unknown as PermissionDescriptor).then((micPermission) => {
        micPermission.onchange = null;
      });
      navigator.permissions.query({ name: 'camera' } as unknown as PermissionDescriptor).then((camPermission) => {
        camPermission.onchange = null;
      });
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStats();
    };
  }, []);

  return {
    room: roomRef.current,
    roomId,
    isRoomInitialized,
    localAudioTrack,
    localVideoTrack,
    localBlurredVideoTrack,
    remoteAudioTrack,
    remoteVideoTrack,
    availableDevices,
    selectedDevices,
    cameraMuted,
    toggleCamera,
    microphoneMuted,
    toggleMicrophone,
    isVideoBlurred,
    applyBlurOnPreCallVideo,
    applyBlurOnInCallVideo,
    TurnCameraOnInCallVideo,
    updateDevice,
    cameraPermissionDenied,
    initRoom,
    setMicrophoneMuted,
    cleanupStats,
    sendFinalStats,
    sendWebRTCStats,
    resetStats,
    initUpgradeRoom,
  };
};

export default useVideoRoom;
